import { z } from 'zod'

/**
 * Organization lifecycle (PLAN v3 §1; "archived" was cut in §6):
 *
 *   draft → published
 *
 * - draft: editable, invisible to the public, nobody can join.
 * - published: the join link is live (registration + KYC open), votings can be created and started.
 *   Test mode (and the slug) are fixed from here on.
 */
export const ORGANIZATION_STATUSES = ['draft', 'published'] as const
export const OrganizationStatusSchema = z.enum(ORGANIZATION_STATUSES)
export type OrganizationStatus = z.infer<typeof OrganizationStatusSchema>

/**
 * Voting ("election") lifecycle, unchanged from v2 (PLAN v2 §0 H4). A voting belongs to an organization.
 *
 *   draft → published → starting → open → closing → ended → results
 *                          ↘ start_failed (retry → starting | cancel → canceled)
 *   any non-final state → canceled
 *
 * - draft: editable, invisible to members.
 * - published: visible to members. With a scheduled start the processes are being / have been created with a
 *   future startDate; with an on-demand start no process exists yet.
 * - starting: the create_processes job is running (one DAVINCI process per question).
 * - open: every question has an open process; voting enabled. Invitations are enqueued on this transition (M3).
 * - closing: end requested / end reached, waiting for the grace window.
 * - ended: processes ended, waiting for on-chain results.
 * - results: results stored for every question.
 * - start_failed: the job gave up; organizer can Retry (resumes) or Cancel.
 * - canceled: terminal.
 */
export const ELECTION_STATUSES = [
  'draft',
  'published',
  'starting',
  'open',
  'closing',
  'ended',
  'results',
  'start_failed',
  'canceled',
] as const
export const ElectionStatusSchema = z.enum(ELECTION_STATUSES)
export type ElectionStatus = z.infer<typeof ElectionStatusSchema>

/** Statuses that are final. */
export const FINAL_ELECTION_STATUSES: readonly ElectionStatus[] = ['results', 'canceled']
/** Statuses in which members can see a voting (member home, public voting page). */
export const VISIBLE_ELECTION_STATUSES: readonly ElectionStatus[] = [
  'published',
  'starting',
  'open',
  'closing',
  'ended',
  'results',
  'start_failed',
  'canceled',
]

/**
 * Member lifecycle (one row per person per organization, re-keyed from v2 "voters"):
 *
 *   registered → kyc_pending → approved → active (account + wallet created)
 *                     ↘ in_review → approved | declined_final | retry_allowed
 *                     ↘ retry_allowed → kyc_pending (new Didit session, max attempts)
 *                     ↘ declined_final (terminal)
 *   any non-revoked → removed (organizer removal; re-admit → back to `removed_from_status`)
 *   any → revoked (organizer reset, or a stale registration superseded by a new one: the row is dead and the
 *         person registers + verifies again; the CSP census slot is kept, M1)
 *
 * Manual review decisions are terminal (M4): later Didit events are logged, never applied.
 */
export const MEMBER_STATUSES = [
  'registered',
  'kyc_pending',
  'in_review',
  'approved',
  'retry_allowed',
  'declined_final',
  'active',
  'removed',
  'revoked',
] as const
export const MemberStatusSchema = z.enum(MEMBER_STATUSES)
export type MemberStatus = z.infer<typeof MemberStatusSchema>

/**
 * Statuses excluded from the per-organization uniqueness of email / national ID / phone / wallet (partial unique
 * indexes). A `declined_final` or `removed` row keeps blocking its email and national ID ("a completed negative
 * result is not retryable"; a removed member cannot re-join by registering again); only an organizer reset
 * (→ `revoked`) frees them.
 */
export const MEMBER_STATUSES_EXCLUDED_FROM_UNIQUENESS: readonly MemberStatus[] = ['revoked']

/**
 * Stale registration supersession: when a new POST /join arrives for the same organization + email or national
 * ID and an existing member row is still `registered` or `kyc_pending` (never completed), the backend sets the old
 * row to `revoked` before inserting the new one. Rows in any other status are NOT superseded.
 */
export const SUPERSEDEABLE_MEMBER_STATUSES: readonly MemberStatus[] = ['registered', 'kyc_pending']

/** Statuses the organizer may remove (everything alive). */
export const REMOVABLE_MEMBER_STATUSES: readonly MemberStatus[] = [
  'registered',
  'kyc_pending',
  'in_review',
  'approved',
  'retry_allowed',
  'declined_final',
  'active',
]

export const QUESTION_TYPES = [
  'single_choice',
  'multiple_choice',
  'approval',
  'rating',
  'ranking',
  'quadratic',
] as const
export const QuestionTypeSchema = z.enum(QUESTION_TYPES)
export type QuestionType = z.infer<typeof QuestionTypeSchema>

/** Didit session statuses, verbatim as the Didit API returns them. */
export const DIDIT_SESSION_STATUSES = [
  'Not Started',
  'In Progress',
  'In Review',
  'Approved',
  'Declined',
  'Abandoned',
  'Expired',
  'Kyc Expired',
  'Resubmitted',
  'Awaiting User',
] as const
export const DiditSessionStatusSchema = z.enum(DIDIT_SESSION_STATUSES)
export type DiditSessionStatus = z.infer<typeof DiditSessionStatusSchema>

/** Result of our policy classification of a Didit outcome (see didit.ts). */
export const DIDIT_OUTCOMES = ['approve', 'review', 'retry', 'final_decline', 'pending'] as const
export const DiditOutcomeSchema = z.enum(DIDIT_OUTCOMES)
export type DiditOutcome = z.infer<typeof DiditOutcomeSchema>

/** How a member's self-declared locality maps to a district of the organization's districting list. */
export const DISTRICT_STATUSES = [
  'not_applicable', // organization has no districting list
  'resolved', // auto-mapped from the list
  'unresolved', // no match: organizer must resolve (review flag)
  'manual', // set by the organizer
] as const
export const DistrictStatusSchema = z.enum(DISTRICT_STATUSES)
export type DistrictStatus = z.infer<typeof DistrictStatusSchema>

/**
 * Organizer review actions on a member (requirement: approve / reject / request more info).
 * `approve_same_person` (PLAN v4 R2): the member is a duplicate face of a REVOKED / REMOVED member of the same
 * organization (same person re-registering): approve AND re-point the new member to the matched member's census slot
 * before activation (body `matchedMemberId`; refused when the matched member is still alive).
 */
export const REVIEW_ACTIONS = ['approve', 'reject', 'request_info', 'approve_same_person'] as const
export const ReviewActionSchema = z.enum(REVIEW_ACTIONS)
export type ReviewAction = z.infer<typeof ReviewActionSchema>

/** Terminal manual decision stored on the member (M4). */
export const REVIEW_DECISIONS = ['approved', 'rejected'] as const
export const ReviewDecisionSchema = z.enum(REVIEW_DECISIONS)
export type ReviewDecision = z.infer<typeof ReviewDecisionSchema>

/**
 * Why a member needs manual review. Produced by the backend policy layer and shown in the dashboard.
 * `didit_*` reasons carry the Didit warning codes in `ReviewReason.details`.
 */
export const REVIEW_REASON_CODES = [
  'didit_in_review', // Didit returned In Review
  'didit_warning', // a warning that our table maps to review
  'face_match_below_auto_approve', // face match score < organizer's auto-approve threshold
  'liveness_low',
  'duplicate_national_id', // same-organization duplicate by document number
  'duplicate_email',
  'duplicate_face', // same-organization face match
  'duplicate_phone',
  'phone_voip', // the phone verified at registration is a VoIP / virtual number (allowed, reviewed)
  'declared_data_mismatch', // name / DOB / id number differ from the document
  'nationality_not_allowed', // OCR nationality ∉ organization countries
  'age_out_of_range', // age at registration outside min/max (the age at each voting start is checked by the CSP)
  'age_unknown',
  'district_unresolved',
  'vpn_detected',
  'ip_country_mismatch',
  'sandbox_synthetic_data', // test (sandbox) verification with null OCR: declared data used as extracted
  // PLAN v4 (modular requirements)
  'identity_slot_conflict', // R1: the member's identity keys resolve to several census slots (organizer decides)
  'citizenship_not_allowed', // document nationality (resolveCitizenship) ∉ accepted regions / countries
  'citizenship_unreadable', // no nationality could be resolved from the document
  'residence_outside', // strongest accepted evidence outside every area (PoA ⇒ final decline, else review)
  'residence_near', // evidence within its uncertainty of an area boundary
  'residence_unknown', // accepted evidence exists but cannot be evaluated (no coordinates, VPN IP, coarse GPS)
  'residence_no_evidence', // no accepted residence evidence was collected
  'residence_conflict', // accepted sources disagree (e.g. PoA inside, document outside)
  'age_estimate_mismatch', // R16: liveness age estimate contradicts the declared date of birth
  'other',
] as const
export const ReviewReasonCodeSchema = z.enum(REVIEW_REASON_CODES)
export type ReviewReasonCode = z.infer<typeof ReviewReasonCodeSchema>

/**
 * Why a member cannot vote in a voting (CSP refusal `not_eligible` details.reason, member home, invitations).
 * Evaluated live (S8: no snapshot) by `memberEligibility()` (domain/eligibility.ts).
 */
export const INELIGIBILITY_REASONS = [
  'member_not_active', // not `active` (no account yet, in review, …)
  'member_removed', // removed by the organizer
  'district_not_eligible', // the voting is restricted to districts that do not include the member's
  'age_out_of_range', // age at the voting start outside the organization's min / max age
  'age_unknown', // no date of birth (extracted or declared) to check the age with
  // PLAN v4 R7 (computed live from the stored evidence and the CURRENT requirements; exempted modules skipped)
  'citizenship_not_eligible', // document nationality not in the organization's current regions / countries
  'residence_outside_area', // residence evidence outside the organization's current areas
  'residence_unverified', // residence evidence near a boundary / not evaluable / none (and no manual decision)
  'requirements_missing_proof', // the current rules need a proof this member never provided (no exemption)
] as const
export const IneligibilityReasonSchema = z.enum(INELIGIBILITY_REASONS)
export type IneligibilityReason = z.infer<typeof IneligibilityReasonSchema>

/** DAVINCI per-process phase as the SDK reports it (`ProcessPhase`), plus our pre-creation states. */
export const PROCESS_PHASES = [
  'not_created', // no processId yet
  'creating', // tx in flight
  'upcoming',
  'open',
  'paused',
  'closing',
  'ended',
  'results',
  'canceled',
] as const
export const ProcessPhaseSchema = z.enum(PROCESS_PHASES)
export type ProcessPhase = z.infer<typeof ProcessPhaseSchema>

/** Sequencer vote status (`VoteStatus` in the SDK) plus `submitted` before the first status poll. */
export const VOTE_STATUSES = ['submitted', 'pending', 'aggregated', 'processed', 'settled', 'error'] as const
export const VoteStatusSchema = z.enum(VOTE_STATUSES)
export type VoteStatus = z.infer<typeof VoteStatusSchema>

/**
 * Background jobs, partitioned in two queues so that emails never block chain transactions (and vice versa):
 * - `chain`: serialized chain writes (one job at a time across instances; per-organization creator wallets plus the
 *   treasury that funds them): create / end / cancel / extend processes, metadata sync, the `finalizeResults`
 *   safety call.
 * - `email`: SMTP sending (rate-limited, retried per recipient): communications, voting invitations and the notices
 *   to the organization's organizer (results waiting for the election committee, results in).
 */
export const JOB_QUEUES = ['chain', 'email'] as const
export const JobQueueSchema = z.enum(JOB_QUEUES)
export type JobQueue = z.infer<typeof JobQueueSchema>

export const JOB_KINDS = [
  'create_processes',
  'end_processes',
  'cancel_processes',
  'extend_processes',
  'sync',
  'finalize_results',
  'send_communication',
  'send_invitations',
  'send_organizer_notice',
] as const
export const JobKindSchema = z.enum(JOB_KINDS)
export type JobKind = z.infer<typeof JobKindSchema>

/** Queue of each job kind (the `jobs.queue` column is set from this at enqueue time). */
export const JOB_KIND_QUEUE: Readonly<Record<JobKind, JobQueue>> = {
  create_processes: 'chain',
  end_processes: 'chain',
  cancel_processes: 'chain',
  extend_processes: 'chain',
  sync: 'chain',
  finalize_results: 'chain',
  send_communication: 'email',
  send_invitations: 'email',
  send_organizer_notice: 'email',
}

export const JOB_STATUSES = ['queued', 'running', 'done', 'failed'] as const
export const JobStatusSchema = z.enum(JOB_STATUSES)
export type JobStatus = z.infer<typeof JobStatusSchema>

/** Statistics-only gender options collected at registration. */
export const GENDERS = ['female', 'male', 'other', 'undisclosed'] as const
export const GenderSchema = z.enum(GENDERS)
export type Gender = z.infer<typeof GenderSchema>

export const DIDIT_MODES = ['live', 'sandbox'] as const
export const DiditModeSchema = z.enum(DIDIT_MODES)
export type DiditMode = z.infer<typeof DiditModeSchema>

/* ------------------------------------------------------------------ email (PLAN v3 §3) */

/** `message`: composed by the organizer. `invitation`: sent once when a voting opens (M3, S9). */
export const COMMUNICATION_KINDS = ['message', 'invitation'] as const
export const CommunicationKindSchema = z.enum(COMMUNICATION_KINDS)
export type CommunicationKind = z.infer<typeof CommunicationKindSchema>

/**
 * - queued: recipients pre-inserted (M3), the send job is waiting.
 * - sending: the email worker is delivering.
 * - sent: every recipient row is final (sent / failed / skipped).
 * - failed: the job gave up (SMTP down for good, misconfiguration); rows not accepted stay `pending`/`failed`.
 */
export const COMMUNICATION_STATUSES = ['queued', 'sending', 'sent', 'failed'] as const
export const CommunicationStatusSchema = z.enum(COMMUNICATION_STATUSES)
export type CommunicationStatus = z.infer<typeof CommunicationStatusSchema>

/**
 * Per-recipient delivery (M3: retries only resend rows that SMTP has not accepted yet).
 * - pending: not accepted by SMTP yet (initial state, or a retryable failure).
 * - sent: accepted by SMTP (never resent).
 * - failed: permanent failure (error stored).
 * - skipped: not sent on purpose (opted out after enqueue, member removed, no email).
 */
export const RECIPIENT_STATUSES = ['pending', 'sent', 'failed', 'skipped'] as const
export const RecipientStatusSchema = z.enum(RECIPIENT_STATUSES)
export type RecipientStatus = z.infer<typeof RecipientStatusSchema>

/** How email is delivered by this deployment (`capture`: SMTP not configured, written to the log / DB only). */
export const EMAIL_TRANSPORTS = ['smtp', 'capture'] as const
export const EmailTransportSchema = z.enum(EMAIL_TRANSPORTS)
export type EmailTransport = z.infer<typeof EmailTransportSchema>

/* ------------------------------------------------------------------ election keys (production beta) */

/**
 * Who holds the key that protects the ballots of a voting (and can therefore unlock its results):
 * - `dkg` ("Automatic"): the DAVINCI key network, an anonymous threshold network of independent operators
 *   (davinci-dkg); results normally appear by themselves minutes after voting ends. The default.
 * - `council` ("Election committee"): a committee of named people created in the Council app; `t` of its `n`
 *   members turn their key once the committee's opening allows it.
 * - `sequencer`: the server-held sequencer key. Never offered in the UI; accepted only behind the conjunctive gate
 *   (ALLOW_SEQUENCER_KEY_MODE=true AND a test organization AND, when set, SEQUENCER_KEY_MODE_ORGS), at every entry
 *   point. Kept in the enum so old rows still parse.
 * Frozen at publish: the mode (and the committee) never change afterwards, retries reuse the persisted values.
 */
export const ELECTION_KEY_MODES = ['dkg', 'council', 'sequencer'] as const
export const ElectionKeyModeSchema = z.enum(ELECTION_KEY_MODES)
export type ElectionKeyMode = z.infer<typeof ElectionKeyModeSchema>
export const DEFAULT_ELECTION_KEY_MODE: ElectionKeyMode = 'dkg'

/**
 * Election committee (Council ceremony) linked to an organization, derived from the chain on every refresh:
 * - forming: the ceremony is still in registration / dealing, or Live without both grants (the registry's Council
 *   adapter allowed AND this organization's creator address authorized). Shown with progress, not selectable.
 * - ready: Live + both grants. Selectable; results unlock once the committee's opening allows it and t members act.
 * - open: ready and its decryption gate is already open (opened manually, or the scheduled / fallback date passed).
 * - unusable: the ceremony was aborted before completion (only reachable from forming: a Live ceremony cannot abort).
 */
export const COUNCIL_STATUSES = ['forming', 'ready', 'open', 'unusable'] as const
export const CouncilStatusSchema = z.enum(COUNCIL_STATUSES)
export type CouncilStatus = z.infer<typeof CouncilStatusSchema>

/** How the committee reached this organization: the one-use pairing code (recommended) or a pasted link. */
export const COUNCIL_LINK_ORIGINS = ['pairing', 'paste'] as const
export const CouncilLinkOriginSchema = z.enum(COUNCIL_LINK_ORIGINS)
export type CouncilLinkOrigin = z.infer<typeof CouncilLinkOriginSchema>

/** A committee's decryption opening: a scheduled date, or manual (its organizer opens; optional fallback date). */
export const COUNCIL_DECRYPTION_MODES = ['scheduled', 'manual'] as const
export const CouncilDecryptionModeSchema = z.enum(COUNCIL_DECRYPTION_MODES)
export type CouncilDecryptionMode = z.infer<typeof CouncilDecryptionModeSchema>

/**
 * Where an ended process's results stand (davinci-sdk `ResultsState`, `getResultsStatus`):
 *   voting → grace → awaiting-request (dkg) | awaiting-opening (council) → decrypting → finalizable → results
 * plus `awaiting-key-holder` (sequencer key), `locked` (dkg-locked, unused here) and `canceled`.
 */
export const DECRYPTION_STATES = [
  'voting',
  'grace',
  'awaiting-key-holder',
  'awaiting-request',
  'locked',
  'awaiting-opening',
  'decrypting',
  'finalizable',
  'results',
  'canceled',
] as const
export const DecryptionStateSchema = z.enum(DECRYPTION_STATES)
export type DecryptionState = z.infer<typeof DecryptionStateSchema>
